import React, { useState } from "react";
import { ExistingSignature } from "../../models/signature";
import { convertFSTimestampToLuxonDT } from "../../utils";
import ChevronDownIcon from "@heroicons/react/24/solid/ChevronDownIcon";
import ChevronRightIcon from "@heroicons/react/24/solid/ChevronRightIcon";

type SignaturesSectionProps = {
  signatures: ExistingSignature[];
  timezone: string;
};

const SignaturesSection: React.FC<SignaturesSectionProps> = ({
  signatures,
  timezone,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Sort signatures by timestamp
  const sortedSignatures = [...signatures].sort(
    (a, b) => a.timestampCreated.toMillis() - b.timestampCreated.toMillis(),
  );

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const signatureCount = signatures.length;
  const summaryText =
    signatureCount === 0
      ? "No signatures found"
      : `${signatureCount} ${signatureCount === 1 ? "Signature" : "Signatures"}`;

  return (
    <div className="mt-2 rounded-lg border border-gray-200">
      {/* Collapsible header */}
      <div
        className="flex cursor-pointer items-center justify-between p-2 hover:bg-gray-50"
        onClick={toggleExpanded}
      >
        <div className="flex items-center">
          {isExpanded ? (
            <ChevronDownIcon className="mr-2 h-5 w-5 text-gray-500" />
          ) : (
            <ChevronRightIcon className="mr-2 h-5 w-5 text-gray-500" />
          )}
          <h3 className="text-lg">Signatures</h3>
        </div>
        <div className="text-sm text-gray-500">{summaryText}</div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-2 pt-0">
          {signatureCount === 0 ? (
            <p className="p-2 text-gray-500">No signatures found</p>
          ) : (
            <div className="space-y-2">
              {sortedSignatures.map((signature) => {
                const lux = convertFSTimestampToLuxonDT(
                  signature.timestampCreated,
                );
                const formattedDate = lux
                  .setZone(timezone)
                  .toFormat("LL/dd/yy hh:mm a");

                return (
                  <div
                    key={signature.id}
                    className="rounded-md border border-gray-200 p-2"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="mr-2 h-8 w-20 overflow-hidden">
                          <img
                            src={`data:image/png;base64, ${signature.imageData}`}
                            alt="Signature thumbnail"
                            className="h-full w-full object-contain"
                          />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            {signature.note} on {formattedDate}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SignaturesSection;
